import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Shield, CheckCircle, Loader2 } from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GoogleAuthButton = ({ onSuccess, className = '' }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleLogin = () => {
    setIsLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Button
      onClick={handleGoogleLogin}
      disabled={isLoading}
      className={`bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 shadow-sm ${className}`}
    >
      {isLoading ? (
        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
      ) : (
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      )}
      Continue with Google
    </Button>
  );
};

const LeadCapture = ({ isOpen, onClose, onSubmit, type = 'start' }) => {
  const [isSuccess, setIsSuccess] = useState(false);

  const isStartType = type === 'start';

  // Handle successful auth from App.js
  const handleAuthSuccess = (userData) => {
    setIsSuccess(true);
    setTimeout(() => {
      onSubmit && onSubmit(userData);
      setIsSuccess(false);
      onClose();
    }, 1500);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        {!isSuccess ? (
          <>
            <DialogHeader className="text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-emerald-600" />
              </div>
              <DialogTitle className="text-2xl font-bold text-slate-900">
                {isStartType ? 'Start Your Free Training' : 'Get Your Certificate'}
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                {isStartType 
                  ? 'Sign in with Google to access the complete OSHA training course'
                  : 'Sign in with Google to download your certificate of completion'
                }
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 space-y-4">
              <GoogleAuthButton className="w-full py-6" />
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Why sign in?</span>
                </div>
              </div>

              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Track your progress across all modules
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Save quiz scores and scenario results
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Get your personalized certificate
                </li>
              </ul>

              <p className="text-xs text-slate-500 text-center pt-2">
                By continuing, you agree to receive updates about WSafe AI™. Unsubscribe anytime.
              </p>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4 animate-bounce">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">You&apos;re In!</h3>
            <p className="text-slate-600">
              {isStartType 
                ? 'Get ready to master workplace safety. Redirecting to your course...'
                : 'Your certificate is being prepared for download...'
              }
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadCapture;
